import { TFunction } from 'i18next';
// api client
import { ApiClient } from './ApiClient';
import { i18nKeys } from '@/src/utils/i18n.utils';

export type GenAiAuditOptions = {
  eventId: string;
  challengeId: string;
  accountNumber: string;
  taskNumber: string;
};

export type GenAiAuditResponse = {
  auditResult: string;
};

export type GenAiAssistantQueryRequest = {
  type?: 'query';
  message: string;
  sessionId?: string;
};

export type GenAiAssistantQueryResponse = {
  answer: string;
  queryId: string;
  /** Conversation id; send it back on the next query to continue the conversation. */
  sessionId?: string;
};

export type GenAiAssistantFeedbackRating = 'positive' | 'negative';

export type GenAiAssistantFeedbackRequest = {
  type: 'feedback';
  queryId: string;
  feedback: GenAiAssistantFeedbackRating;
  comment?: string;
};

export type GenAiAssistantFeedbackResponse = {
  queryId: string;
  feedback: GenAiAssistantFeedbackRating;
  commentReceived?: boolean;
};

/**
 * Thrown when the feedback target (queryId) is unknown or older than 30 days
 * (HTTP 404 FeedbackTargetNotFoundError).
 */
export class FeedbackTargetNotFoundError extends Error {
  constructor() {
    super('FEEDBACK_TARGET_NOT_FOUND');
    this.name = 'FeedbackTargetNotFoundError';
  }
}

/**
 * A single message in an AI Teammate conversation, as returned by the backend.
 */
export type TeammateChatMessage = {
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  feedbackRating?: number | null;
  asyncInProgress?: boolean;
};

/**
 * Response from posting a message to the AI Teammate.
 * When `tool` is not `'respond'` and `status` is `'processing'`, the final answer is
 * fetched asynchronously from `s3Url`.
 */
export type PostAiTeammateChatResponse = {
  jobId?: string;
  sessionId: string;
  message?: string;
  tool?: string;
  status?: 'processing' | 'done' | string;
  s3Url?: string;
};

/**
 * Response from fetching AI Teammate chat history.
 */
export type TeammateChatHistoryResponse = {
  sessionId: string;
  messages?: TeammateChatMessage[];
  nextToken?: string;
  pendingConfirmation?: { type: string; challengeId: string };
};

export type GenAiAssistantHistoryMessage = {
  messageId: string;
  timestamp: string;
  role: 'user' | 'assistant';
  content: string;
};

export type GenAiAssistantHistoryResponse = {
  /** Null when the user has no assistant conversation yet. */
  sessionId: string | null;
  messages: GenAiAssistantHistoryMessage[];
  nextToken: string | null;
};

export class GenAiAPI {
  constructor(private apiClient: ApiClient, private t: TFunction) {
    // do nothing
  }

  public async createNewAuditRequest(options: GenAiAuditOptions) {
    const response = (await this.apiClient.post({
      path: '/llm/task/audit',
      body: options,
      failMessage: 'An Error has ocurred',
    })) as GenAiAuditResponse;
    return response;
  }

  public async getAIPARReport(eventName: string) {
    const response = (await this.apiClient.post({
      path: '/llm/reports/parreport',
      body: { eventName },
      failMessage: this.t(i18nKeys.errors.requestFailed.report.generateAITeamReport),
    })) as { message?: string; download_url: string };
    return response;
  }

  /**
   * Load the caller's Jam Assistant conversation. The assistant shares the AI Teammate's
   * history endpoint, selected with `channel=assistant`. Without a sessionId the most
   * recent conversation is returned.
   */
  public async getAssistantHistory(sessionId?: string, nextToken?: string): Promise<GenAiAssistantHistoryResponse> {
    const params: Record<string, string> = { channel: 'assistant' };
    if (sessionId) {
      params.sessionId = sessionId;
    }
    if (nextToken) {
      params.nextToken = nextToken;
    }
    const response = (await this.apiClient.get({
      path: '/llm/teammate/history',
      params,
      errorHandled: true,
    })) as Partial<GenAiAssistantHistoryResponse> | undefined;
    // The service may omit fields (e.g. no `messages` for an empty conversation); normalize
    // so callers can always iterate `messages` and null-check the rest.
    return {
      sessionId: response?.sessionId ?? null,
      messages: response?.messages ?? [],
      nextToken: response?.nextToken ?? null,
    };
  }

  public async queryAssistant(message: string, sessionId?: string) {
    try {
      const response = (await this.apiClient.post({
        path: '/llm/assistant/query',
        body: sessionId ? { type: 'query', message, sessionId } : { type: 'query', message },
        errorHandled: true,
      })) as GenAiAssistantQueryResponse;
      return response;
    } catch (error: any) {
      if (error?.status === 427) {
        const rateLimitError = new Error('RATE_LIMIT_EXCEEDED');
        rateLimitError.name = 'RateLimitError';
        throw rateLimitError;
      }
      throw error;
    }
  }

  /**
   * Submit thumbs up/down feedback for a previous assistant response.
   * Posts to the same endpoint as the query call, discriminated by `type: 'feedback'`.
   *
   * @throws FeedbackTargetNotFoundError when the backend returns 404 (unknown or expired queryId).
   */
  public async submitAssistantFeedback(options: {
    queryId: string;
    feedback: GenAiAssistantFeedbackRating;
    comment?: string;
  }) {
    const body: GenAiAssistantFeedbackRequest = {
      type: 'feedback',
      queryId: options.queryId,
      feedback: options.feedback,
    };
    // Only include a comment when it is a non-empty string. Never send an empty string.
    const trimmedComment = options.comment?.trim();
    if (trimmedComment) {
      body.comment = trimmedComment;
    }

    try {
      const response = (await this.apiClient.post({
        path: '/llm/assistant/query',
        body,
        errorHandled: true,
      })) as GenAiAssistantFeedbackResponse;
      return response;
    } catch (error: any) {
      if (error?.status === 404) {
        throw new FeedbackTargetNotFoundError();
      }
      throw error;
    }
  }

  public postAiTeammateChat(
    eventName: string,
    vibeId: string,
    prompt: string,
    sessionId?: string,
    challengeId?: string,
    teamId?: string
  ): Promise<PostAiTeammateChatResponse> {
    return this.apiClient.post({
      path: '/llm/teammate',
      body: {
        prompt,
        eventName,
        eventId: eventName,
        vibeId,
        sessionId,
        challengeId,
        teamId,
      },
    }) as Promise<PostAiTeammateChatResponse>;
  }

  public getTeammateChatHistory(
    eventId: string,
    sessionId?: string | null,
    maxResults?: number | null,
    nextToken?: string | null
  ): Promise<TeammateChatHistoryResponse> {
    return this.apiClient.get({
      path: '/llm/teammate/history',
      params: {
        eventId,
        ...(sessionId ? { sessionId } : {}),
        ...(maxResults ? { maxResults } : {}),
        ...(nextToken ? { nextToken } : {}),
      },
      errorHandled: false,
    }) as Promise<TeammateChatHistoryResponse>;
  }
}
